import * as React from 'react'

import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { classList } from 'react-classlist-helper'

interface DialogRootProps {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  modal?: boolean
  dismissible?: boolean
  className?: string
  // Override the portal mount point so the popup joins a custom stacking context — needed when nesting inside another portal layer.
  portalContainer?: React.RefObject<HTMLElement | null>
  initialFocus?: React.RefObject<HTMLElement | null>
}

function DialogRoot({
  open,
  onClose,
  children,
  modal = true,
  dismissible = true,
  className,
  portalContainer,
  initialFocus,
}: DialogRootProps) {
  return (
    <BaseDialog.Root
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          onClose()
        }
      }}
      modal={modal}
      disablePointerDismissal={!dismissible}
    >
      <BaseDialog.Portal container={portalContainer}>
        <BaseDialog.Backdrop className="tw:fixed tw:inset-0 tw:z-50 tw:bg-black/30" />
        <BaseDialog.Popup
          className={classList({
            'tw:fixed tw:z-50 tw:top-1/2 tw:left-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:bg-md3-surface tw:shadow-lg tw:w-[400px] tw:max-w-[90vw] tw:rounded-lg tw:p-6': true,
            [className]: !!className,
          })}
          initialFocus={initialFocus}
        >
          {children}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  )
}

interface DialogTitleProps {
  children: React.ReactNode
  className?: string
}

function DialogTitle({ children, className }: DialogTitleProps) {
  return (
    <BaseDialog.Title
      className={classList({
        'tw:m-0 tw:text-headline-sm tw:text-md3-on-surface': true,
        [className]: !!className,
      })}
    >
      {children}
    </BaseDialog.Title>
  )
}

interface DialogDescriptionProps {
  children: React.ReactNode
  className?: string
}

function DialogDescription({ children, className }: DialogDescriptionProps) {
  return (
    <BaseDialog.Description
      className={classList({
        'tw:text-body-md tw:text-md3-on-surface': true,
        [className]: !!className,
      })}
    >
      {children}
    </BaseDialog.Description>
  )
}

interface DialogBodyProps {
  children: React.ReactNode
  className?: string
}

function DialogBody({ children, className }: DialogBodyProps) {
  return (
    <div
      className={classList({
        'tw:py-4': true,
        [className]: !!className,
      })}
    >
      {children}
    </div>
  )
}

interface DialogActionsProps {
  children: React.ReactNode
  className?: string
}

function DialogActions({ children, className }: DialogActionsProps) {
  return (
    <div
      className={classList({
        'tw:flex tw:gap-3 tw:justify-end': true,
        [className]: !!className,
      })}
    >
      {children}
    </div>
  )
}

function DialogClose({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <BaseDialog.Close
      className={classList({
        'tw:absolute tw:top-2 tw:right-2 tw:flex tw:items-center tw:justify-center tw:size-[40px] tw:shrink-0 tw:border-none tw:bg-transparent tw:cursor-pointer tw:text-md3-on-surface-variant': true,
        [className]: !!className,
      })}
    >
      {children}
    </BaseDialog.Close>
  )
}

export const Dialog = {
  Root: DialogRoot,
  Title: DialogTitle,
  Description: DialogDescription,
  Body: DialogBody,
  Actions: DialogActions,
  Close: DialogClose,
}
